<template>
  <v-container fluid>
    <v-tabs v-model="activeTab">
      <v-tab>
        Calculate Charges
      </v-tab>
      <v-tab>
        Past Charge Reports
      </v-tab>
      <v-tab-item>
        <v-spacer />
        <!-- <v-divider /> -->
        <hr>
        <client-auto-complete
          label-text="Select a client"
          :allow-new="false"
          @onClickClient="clientSelected"
        />
        <v-select
          v-if="client && client.clientId && visible"
          ref="subClientSelect"
          v-model="selectedSubClients"
          :items="subClientItems"
          item-text="desc"
          item-value="subClientId"
          return-object
          multiple
          chips
          label="Select sub clients"
        >
          <template v-slot:prepend-item>
            <v-list-item
              ripple
              @mousedown.prevent
              @click="selectAllSubClients"
            >
              <v-list-item-content>
                <v-list-item-title>
                  Select All
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-divider class="mt-2" />
          </template>
          <template v-slot:selection="{item, index}">
            <v-chip v-if="index < 4">
              <span>{{ item.name }}</span>
            </v-chip>
            <span
              v-if="index === 4"
              class="grey--text text-caption"
            >
              (+{{ selectedSubClients.length - 1 }} others)
            </span>
          </template>
          <template v-slot:prepend-item>
            <v-btn
              color="primary"
              @click="focusOnAutocomplete"
            >
              Apply
            </v-btn>
          </template>
        </v-select>
        <v-autocomplete
          v-model="selectedItems"
          :items="items"
          :loading="searchLoading"
          :search-input.sync="search"
          hide-no-data
          hide-selected
          allow-overflow
          menu-props="auto, overflowY"
          label="Search by ICN"
          dense
          item-text="name"
          item-value="item"
          prepend-icon="mdi-magnify"
          return-object
          chips
          small-chips
          deletable-chips
          multiple
        />
        <template>
          <div class="text-center">
            <v-btn
              :loading="pdfLoading"
              color="primary"
              dark
              @click="getPdf"
            >
              Print Table
            </v-btn>
            <v-dialog
              v-model="totalsDialog"
              width="500"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="red lighten-2"
                  dark
                  v-bind="attrs"
                  v-on="on"
                >
                  Totals
                </v-btn>
              </template>
              <v-card>
                <v-card-text>
                  <v-row>
                    <v-col
                      align-self="start"
                    >
                      Size Charges:
                    </v-col>
                    <v-col
                      align-self="end"
                    >
                      <strong>{{ sizeChargeTotal | currency }}</strong>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col
                      align-self="start"
                    >
                      Services Total:
                    </v-col>
                    <v-col
                      align-self="end"
                    >
                      <strong>{{ additionalServicesTotal | currency }}</strong>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col
                      align-self="start"
                    >
                      Materials Total:
                    </v-col>
                    <v-col
                      align-self="end"
                    >
                      <strong>{{ additionalMaterialsTotal | currency }}</strong>
                    </v-col>
                  </v-row>
                  <v-divider />
                  <v-row>
                    <v-col
                      align-self="start"
                    >
                      Grand Total:
                    </v-col>
                    <v-col
                      align-self="end"
                    >
                      <span style="color:red;">
                        <strong>{{ grandTotal | currency }}</strong>
                      </span>
                    </v-col>
                  </v-row>
                  <v-row class="justify-center">
                    <v-btn
                      text
                      @click="totalsDialog = false"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      color="primary"
                      class="text-center"
                      dark
                      @click="showConfirmDialog"
                    >
                      Mark Items as Billed
                    </v-btn>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-dialog>
            <v-dialog
              v-model="confirmDialog"
              width="500"
            >
              <v-card>
                <v-card-text>
                  <strong>
                    You are about to deactivate these items.
                    This action cannot be undone. Do you want to proceed?
                  </strong>
                </v-card-text>
                <v-card-actions
                  v-if="!success"
                >
                  <v-row class="justify-center">
                    <v-btn
                      text
                      @click="closeConfirmDialog"
                    >
                      No
                    </v-btn>
                    <v-btn
                      color="primary"
                      class="text-center"
                      dark
                      @click="markItemsBilled"
                    >
                      Yes
                    </v-btn>
                  </v-row>
                </v-card-actions>
                <v-card-text
                  v-if="success"
                  style="height:1%"
                >
                  Item(s) successfully billed and deactivated:
                  <v-divider />
                  <ul>
                    <li
                      v-for="(v) in charges"
                      :key="v.item.itemId"
                    >
                      <a :href="'/inventory/' + v.item.itemId">{{ v.item.icn }}</a>
                    </li>
                  </ul>
                </v-card-text>
                <v-card-actions
                  v-if="success"
                >
                  <v-row class="justify-center">
                    <v-btn
                      color="primary"
                      class="text-center"
                      dark
                      @click="reset"
                    >
                      Ok
                    </v-btn>
                  </v-row>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </div>
        </template>
        <v-card>
          <v-card-text>
            <v-container>
              <v-row>
                <v-menu
                  ref="startDateMenu"
                  v-model="startDateMenu"
                  :close-on-content-click="false"
                  :return-value.sync="date"
                  transition="scale-transition"
                  max-width="290"
                  offset-y
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="date"
                      v-bind="attrs"
                      label="Calculate Charges on Date"
                      clearable
                      @click:clear="date = null"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="date"
                    width="290"
                    scrollable
                    @change="saveStartDate"
                  />
                </v-menu>
              </v-row>
            </v-container>
          </v-card-text>
        </v-card>
        <v-data-table
          ref="tableContent"
          :headers="headers"
          :items="charges"
          :items-per-page="-1"
          class="elevation-1"
        >
          <template v-slot:item.palletDisposalCharge="{ item }">
            <span>{{ item.palletDisposalCharge | currency }}</span>
          </template>
          <template v-slot:item.sizeCharge="{ item } ">
            <span>{{ item.sizeCharge | currency }}</span>
          </template>
          <template v-slot:item.additionalServices="{ item }">
            <span>{{ item.additionalServices | currency }}</span>
          </template>
          <template v-slot:item.additionalMaterials="{ item }">
            <span>{{ item.additionalMaterials | currency }}</span>
          </template>
          <template v-slot:item.totalCharge="{ item }">
            <span>{{ item.totalCharge | currency }}</span>
          </template>
          <template v-slot:item.actions="props">
            <td
              class="justify-center"
              layout
              px-0
            >
              <v-btn
                text
                color="teal accent-4"
                @click="goToItem(props.item)"
              >
                Edit
              </v-btn>
            </td>
          </template>
        </v-data-table>
      </v-tab-item>
      <v-tab-item>
        <v-row>
          <v-col
            cols="12"
            lg="6"
          >
            <v-menu
              ref="pastReportsStartDateMenu"
              v-model="pastReportsStartDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="pastReportsStartDateFormatted"
                  label="Start Date"
                  persistent-hint
                  prepend-icon="mdi-calendar"
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="pastReportsStartDate"
                no-title
                :max="new Date().toISOString().substr(0, 10)"
                @input="pastReportsStartDateMenu = false"
              />
            </v-menu>
          </v-col>

          <v-col
            cols="12"
            lg="6"
          >
            <v-menu
              ref="pastReportsEndDateMenu"
              v-model="pastReportsEndDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="pastReportsEndDateFormatted"
                  label="End Date"
                  persistent-hint
                  prepend-icon="mdi-calendar"
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="pastReportsEndDate"
                no-title
                :max="new Date().toISOString().substr(0, 10)"
                @input=" pastReportsEndDateMenu = false"
              />
            </v-menu>
          </v-col>
        </v-row>
        <v-row
          justify="center"
        >
          <v-btn
            text
            color="primary"
            dark
            @click="getPastReports"
          >
            Search
          </v-btn>
        </v-row>
        <v-data-table
          :headers="pastReportHeaders"
          :items="pastReports"
          :items-per-page="-1"
          :loading="pastReportsLoading"
          class="elevation-1"
          @click:row="getPastReport"
        >
          <template v-slot:item.chargeDate="{ item }">
            {{ item.chargeDate.substr(0, 10) }}
          </template>
          <template v-slot:item.grandTotal="{ item }">
            {{ item.grandTotal | currency }}
          </template>
        </v-data-table>
      </v-tab-item>
    </v-tabs>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex';
import { readPageQuery, replacePageQuery } from '../utils/pageQuery';

import ClientAutoComplete from './ClientAutoComplete.vue';

export default {
  name: 'InventoryChargesComponent',
  components: {
    ClientAutoComplete,
  },
  data: (vm) => ({
    activeTab: Math.max(0, ['calculate', 'past'].indexOf(vm.$route.query.tab)),
    visible: true,
    selectedItems: [],
    searchLoading: false,
    pdfLoading: false,
    entries: [],
    search: null,
    date: null,
    startDateMenu: false,
    successDialog: false,
    errorDialog: false,
    confirmDialog: false,
    success: false,
    timerId: null,
    test: 'table',
    totalsDialog: false,
    client: null,
    availableSubClients: [],
    selectedSubClients: [],
    pastReportsStartDate: new Date(Date.now()).toISOString().substr(0, 10),
    pastReportsEndDate: new Date(Date.now()).toISOString().substr(0, 10),
    pastReports: [],
    pastReportsLoading: false,
    pastReportsStartDateMenu: false,
    pastReportsEndDateMenu: false,
    pastReportHeaders: [
      {
        text: 'Client',
        value: 'client.icnAbbreviation',
      },
      {
        text: 'SubClient',
        value: 'subClient.icnAbbreviation',
      },
      {
        text: 'Piece Count',
        value: 'pieceCount',
      },
      {
        text: 'Grand Total',
        value: 'grandTotal',
      },
      {
        text: 'Date',
        value: 'chargeDate',
      },
    ],
    charges: [],
    headers: [
      {
        text: 'Warehouse',
        value: 'item.warehouse.name',
      },
      {
        text: 'LOC',
        value: 'item.warehouseLocation.name',
      },
      {
        text: 'ICN',
        value: 'item.icn',
      },
      {
        text: 'Received',
        value: 'receivedDate',
      },
      {
        text: 'Charges Start',
        value: 'chargeStartDate',
      },
      {
        text: 'Description',
        value: 'description',
      },
      {
        text: 'Pallet/Crate Disposal',
        value: 'palletDisposalCharge',
      },
      {
        text: 'Storage charge',
        value: 'sizeCharge',
      },
      {
        text: 'Additional Services',
        value: 'additionalServices',
      },
      {
        text: 'Additional Materials',
        value: 'additionalMaterials',
      },
      {
        text: 'Total',
        value: 'totalCharge',
      },
      {
        text: '',
        value: 'actions',
        sortable: 'false',
      },
    ],
  }),
  computed: {
    pastReportsStartDateFormatted() {
      return this.formatDate(this.pastReportsStartDate);
    },
    pastReportsEndDateFormatted() {
      return this.formatDate(this.pastReportsEndDate);
    },
    fields() {
      if (!this.entries) {
        return [];
      }

      return Object.keys(this.selectedItems).map((key) => ({
        key,
        value: this.selectedItems[key] || 'n/a',
      }));
    },
    items() {
      return this.entries.map((entry) => (entry));
    },
    subClientItems() {
      return this.availableSubClients.map((subClient) => {
        const desc = `${subClient.name} (${subClient.icnAbbreviation})`;
        return { ...subClient, desc };
      });
    },
    additionalServicesTotal() {
      return this.sumField('additionalServices');
    },
    additionalMaterialsTotal() {
      return this.sumField('additionalMaterials');
    },
    sizeChargeTotal() {
      return this.sumField('sizeCharge');
    },
    grandTotal() {
      return this.sumField('totalCharge');
    },
  },
  watch: {
    activeTab(index) {
      const tab = ['calculate', 'past'][index];
      const query = readPageQuery();
      if (tab === 'calculate') {
        delete query.tab;
      } else {
        query.tab = tab;
      }
      replacePageQuery(query);
    },
    search(val) {
      this.searchDebounced(val);
    },
    selectedItems(val, oldVal) {
      this.search = '';

      const adds = [
        ...val.filter((x) => !oldVal.includes(x)),
      ];

      const removes = [
        ...oldVal.filter((x) => !val.includes(x)),
      ];

      if (removes.length > 0) {
        this.charges = this.charges.filter((c) => !removes
          .map((r) => r.id).includes(c.item.itemId));
      }

      if (adds.length > 0) {
        adds.forEach((a) => this.getCharge(a.id));
      }
    },
  },
  created() {
    const endDate = new Date(Date.now());
    endDate.setFullYear(endDate.getFullYear() - 1);
    this.pastReportsStartDate = endDate.toISOString().substr(0, 10);

    this.getPastReports();
  },
  methods: {
    ...mapActions('apiClient', ['get', 'post', 'getFile']),
    formatDate(date) {
      if (!date) return null;

      const [year, month, day] = date.split('-');
      return `${month}/${day}/${year}`;
    },
    parseDate(date) {
      if (!date) return null;

      const [month, day, year] = date.split('/');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    },
    sumField(key) {
      return this.charges.reduce((a, b) => a + (b[key] || 0), 0);
    },
    async searchDebounced(query) {
      clearTimeout(this.timerId);

      this.timerId = setTimeout(() => {
        this.doSearch(query);
      }, 300);
    },
    selectAllSubClients() {
      this.$nextTick(() => {
        if (this.selectedSubClients.length === this.availableSubClients.length) {
          this.selectedSubClients = [];
        } else {
          this.selectedSubClients = this.availableSubClients.slice();
        }
      });
    },
    async doSearch(query) {
      if (!query) {
        return;
      }

      this.searchLoading = true;

      const dto = {
        icn: query,
        active: true,
        clientId: this.client.clientId,
        subClientIds: this.selectedSubClients.map((c) => c.subClientId),
      };

      await this.post({ url: '/inventory/autocomplete', body: dto }).then(
        (response) => {
          if (response.data !== null && response.data.length > 0) {
            this.entries = response.data;
          }
        },
        (error) => {
          console.log(error);
        },
      );

      this.searchLoading = false;
    },
    saveStartDate(date) {
      this.$refs.startDateMenu.save(date);
      this.date = date;
      this.getCharges();
    },
    async getPdf() {
      this.pdfLoading = true;

      if (!this.charges) {
        return;
      }

      const chargeIds = {
        ids: [],
      };

      chargeIds.ids = this.charges.map((c) => c.item.itemId);

      let url = '/inventory/charges/download';

      if (this.date) {
        url += `?date=${this.date}`;
      }

      await this.post({ url, body: chargeIds }).then(
        (response) => {
          const arrayBuffer = this.base64ToArrayBuffer(response.data);
          this.createAndDownloadBlobFile(arrayBuffer, 'charge-report.pdf');
        },
        (error) => {
          console.log(error);
        },
      );

      this.pdfLoading = false;
    },
    base64ToArrayBuffer(data) {
      const bString = window.atob(data);
      const bLength = bString.length;
      const bytes = new Uint8Array(bLength);
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < bLength; i++) {
        const ascii = bString.charCodeAt(i);
        bytes[i] = ascii;
      }
      return bytes;
    },
    createAndDownloadBlobFile(body, filename) {
      const blob = new Blob([body]);
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    async getPastReport(report) {
      await this.get({ url: `/chargeReports/${report.reportId}` }).then(
        (response) => {
          const arrayBuffer = this.base64ToArrayBuffer(response.data);
          this.createAndDownloadBlobFile(arrayBuffer, 'charge-report.pdf');
        },
        (error) => {
          console.log(error);
        },
      );
    },
    async markItemsBilled() {
      if (!this.charges) {
        return;
      }

      const chargeIds = {
        ids: [],
      };

      chargeIds.ids = this.charges.map((c) => c.item.itemId);

      await this.post({ url: '/inventory/billed', body: chargeIds }).then(
        () => {
          this.success = true;
        },
        (error) => {
          console.log(error);
        },
      );
    },
    clientSelected(client) {
      this.client = client;
      this.getSubClients(client.clientId);
    },
    async getSubClients(clientId) {
      await this.get({ url: `/clients/${clientId}/subClients/options` }).then(
        (response) => {
          this.availableSubClients = response.data;
        },
      );
    },
    async getCharges() {
      if (!this.charges) {
        return;
      }

      const chargeIds = {
        ids: [],
      };

      chargeIds.ids = this.charges.map((c) => c.item.itemId);

      let url = '/inventory/charges';

      if (this.date) {
        url += `?date=${this.date}`;
      }

      await this.post({ url, body: chargeIds, params: { date: this.date } }).then(
        (response) => {
          this.charges = response.data;
        },
        (error) => {
          console.log(error);
        },
      );
    },
    async getCharge(itemId) {
      if (!itemId) {
        return;
      }

      await this.get({ url: `/inventory/${itemId}/charges`, params: { date: this.date } }).then(
        (response) => {
          this.charges.push(response.data);
        },
        (error) => {
          console.log(error);
        },
      );
    },
    async getPastReports() {
      this.pastReportsLoading = true;

      await this.get({ url: `/chargeReports/search?startDate=${this.pastReportsStartDate}&endDate=${this.pastReportsEndDate}` }).then(
        (response) => {
          this.pastReports = response.data;
        },
        (error) => {
          console.log(error);
        },
      );

      this.pastReportsLoading = false;
    },
    goToItem(item) {
      const route = this.$router.resolve({ name: 'inventoryEdit', params: { itemId: item.item.itemId } });
      window.open(route.href, '_blank');
    },
    showConfirmDialog() {
      this.confirmDialog = true;
    },
    closeConfirmDialog() {
      this.confirmDialog = false;
    },
    reset() {
      this.$router.go();
    },
    focusOnAutocomplete() {
      this.visible = false;
    },
  },
};
</script>
